import {
    all,
    call,
    getContext,
    put,
    select,
    setContext,
    take,
    takeEvery,
    delay,
} from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { initAddressRelationsWatchers } from '@elab/core/utils/addressRelations/saga';
import { getActions as getDisplayOneByOneActions } from '@elab/components/connected/DisplayOneByOne/redux';
import {
    actions as cartActions,
    api as cartApi,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import {
    actions as globalActions,
    selectors as globalSelectors,
    types as globalTypes,
} from '@elab/core/store/global';
import { getSearchParam } from '@elab/core/utils/address';
import { createWatcher } from '@elab/core/store/core/saga';
import { getEGCcartId } from '@elab/core/store/cart/saga';
import { api as deliveryAddressApi } from '@elab/components/connected/forms/DeliveryAddressForm/redux';
import { selectors as userSelectors } from '@elab/core/store/user';
import * as configSelectors from '@elab/core/store/config/selectors';
import {
    actions as uiActions,
    selectors as uiSelectors,
    types as uiTypes,
} from '@elab/core/store/ui';
import addressApi from '@elab/core/utils/address/api';
import {
    defineAdditionalAddressFieldsDisplay,
    getConfirmationFromModal,
    initAddressSuggestionWatchers,
    setFakeValidationOnAddressTypeChange,
} from '@elab/core/utils/address/sagaHelpers';
import addressActions from '@elab/core/store/address/actions';
import {
    addressTypes,
    checkoutAddressEditFrom,
    checkoutAddressForm,
} from '@elab/core/utils/constants';
import { handleCloseDialog } from '@elab/core/utils/displayOneByOneHelper';
import { actions as checkoutActions } from '@elab/core/store/checkout';
import { getValueByStorePath } from '@elab/core/utils/helpers';

import api from './api';

import { getActions, getSelectors, getTypes } from '.';

export function* billingSubmit(dto, ctx) {
    const cartId = yield select(cartSelectors.getGuid);
    const userId = yield select(userSelectors.getUserId);

    const isEgiftCardCartActive = !!(yield call(getEGCcartId));

    yield call(api.setBillingAddress, userId, cartId, dto, isEgiftCardCartActive);
    yield put(cartActions.loadCart());
    yield take(cartTypes.SET_CART_DATA);

    if (userId) {
        yield put(addressActions.loadBillingAddresses());
    }

    // if a new billing address is added, uncheck deliveryAsBilling
    yield put(checkoutActions.sagaSetAddressForBilling({ value: false }));

    yield put(ctx.actions.resetFormData());
    yield put(uiActions.closeDialog());
}

export function* deliverySubmit(dto, ctx) {
    const cartId = yield select(cartSelectors.getCartId);
    const userId = yield select(userSelectors.getUserId);
    const addQueryParamCountry = yield select(configSelectors.getAddQueryParamCountry);

    if (addQueryParamCountry) {
        const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
        yield call(deliveryAddressApi.setDeliveryAddress, userId, cartId, dto, deliveryCountry);
    } else {
        yield call(deliveryAddressApi.setDeliveryAddress, userId, cartId, dto);
    }

    yield put(cartActions.loadCart());
    yield take(cartTypes.SET_CART_DATA);

    if (userId) {
        yield put(addressActions.loadAddresses());
    }

    yield put(ctx.actions.resetFormData());
    yield put(uiActions.closeDialog());
}

export function* handleOpenDialog() {
    const openedDialog = yield select(uiSelectors.getDialogName);
    const dialogName = yield getContext('dialogName');

    if (openedDialog === dialogName) {
        const { storeName, actions, selectors } = yield getContext('ctx');
        const displayOneByOneActions = getDisplayOneByOneActions(storeName);
        yield put(displayOneByOneActions.resetItemsToShow());
        yield put(actions.resetFormData());
        const setFakeValidationAddressType = yield select(
            configSelectors.setFakeValidationAddressType,
        );
        if (setFakeValidationAddressType && dialogName === checkoutAddressForm.billingForm) {
            yield call(setFakeValidationOnAddressTypeChange, selectors, actions);
        }
    }
}

export const initializeAddressForm = ({ actions, types }) =>
    function*() {
        // before prefilling the metadata should be received
        yield take(types.SET_INITIAL_DATA);

        const id = yield call(getSearchParam, 'id');

        // means that user wants to update existing address, but not to create a new one
        if (id) {
            yield put(actions.setEditing({ id }));
        }
    };

export const setEditing = ctx =>
    function*({ payload: { id } }) {
        const address = yield call(addressApi.getAddressByID, id);
        if (typeof address !== 'undefined') {
            const prefill = yield call(prefillFormWithData, ctx);
            yield call(prefill, address);
        }
    };

export const prefillFormWithData = ({ storeName, actions, selectors }) =>
    function*(address) {
        const formFields = yield select(selectors.getData);
        const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);
        const isMobileNumber = yield select(configSelectors.setMobileNumber);

        // On the frontend we use isocode as a value in form country dropdowns,
        // but from the back end we receive the country object as a current value
        // in order to form's logic work properly we need to replace country object with isocode property.
        if (address.country) {
            address.country = address.country.isocode;
        }

        // If we should clean phone number we still need show id with mask applied when prefill
        if (shouldCleanPhoneNumber && address.formattedPhone) {
            const fieldValue = isMobileNumber ? 'mobileNumber' : 'phone';
            address[fieldValue] = address.formattedPhone;
        }

        yield call(defineAdditionalAddressFieldsDisplay, {
            storeName,
            valuesSource: address,
        });
        yield all(
            Object.keys(formFields)
                .filter(field => address[field])
                .map(field => {
                    const value =
                        field === 'title' ? address.titleCode || address.title : address[field];
                    return put(actions.setDataFieldValue(field, value, true, true));
                }),
        );
        const preFillCompanyOptionsFormFields = yield select(
            configSelectors.preFillCompanyOptionsFormFields,
        );
        if (preFillCompanyOptionsFormFields && address.addressType === 'CORPORATE') {
            const companyOptionsFormFields = formFields.companyOptionsForm
                ? formFields.companyOptionsForm.fields
                : [];
            yield all(
                companyOptionsFormFields.map((field, index) => {
                    const value = address[field.name] || '';
                    const fieldName = `companyOptionsForm.fields.${index}`;
                    return put(actions.setDataFieldValue(fieldName, value, true, true));
                }),
            );
        }
    };

export const openFormInDialog = ({ storeName, actions }) =>
    function*(action) {
        const {
            id,
            addressType,
            radioView,
            useCartUrlForEdit,
            useCartUrlForAdd,
            submitEditMethod,
            selectForPaymentAfterEditing,
        } = action.payload;
        const messages = yield select(globalSelectors.getMessages);
        const userId = yield select(userSelectors.getUserId);
        const cartId = yield select(cartSelectors.getCartId);
        const addressPopup = checkoutAddressEditFrom[storeName];
        const messageToRemove = messages.find(message => message.id === 'addressBookMessage');
        const isDelivery = addressType === 'shippingAddress';

        const cartSubmitURL = `${cartApi.getApiName(userId)}${cartApi.getCartApiName(
            userId,
            cartId,
        )}/addresses/${isDelivery ? 'delivery' : 'billing'}`;

        if (messageToRemove) {
            yield put(globalActions.removeMessage(messageToRemove));
        }
        yield put(actions.reset());
        yield put(actions.resetFormData());
        if (id) {
            yield put(actions.setEditing({ id }));
        }
        if (addressType) {
            yield put(actions.setAddressType({ addressType }));
        }
        if (radioView) {
            yield put(actions.setRadioView({ radioView }));
        }
        if (useCartUrlForEdit) {
            yield put(actions.setSubmitEditUrl({ submitEditUrl: cartSubmitURL }));
        }
        if (useCartUrlForAdd) {
            yield put(actions.setSubmitAddUrl({ submitAddUrl: cartSubmitURL }));
        }
        if (submitEditMethod) {
            yield put(actions.setSubmitEditMethod({ submitEditMethod }));
        }
        if (selectForPaymentAfterEditing) {
            yield put(actions.setSelectForPaymentAfterEditing({ selectForPaymentAfterEditing }));
        }
        yield put(uiActions.openDialog({ name: addressPopup }));
    };

export function* onSetDataFieldValue({ payload }) {
    const { actions, selectors } = yield getContext('ctx');
    const preFillCompanyOptionsFormFields = yield select(
        configSelectors.preFillCompanyOptionsFormFields,
    );
    if (preFillCompanyOptionsFormFields && payload.name === 'companyName') {
        if (payload.value !== null) {
            yield put(actions.setDataFieldValue('firstName', payload.value));
            yield put(actions.setDataFieldValue('lastName', payload.value));
        }
        return;
    }
    if (payload && payload.name !== 'addressType') {
        return;
    }
    const setFakeValidationAddressType = yield select(configSelectors.setFakeValidationAddressType);
    if (setFakeValidationAddressType) {
        yield call(setFakeValidationOnAddressTypeChange, selectors, actions);
    }
    if (payload.value === addressTypes.INDIVIDUAL) {
        if (preFillCompanyOptionsFormFields) {
            yield put(actions.setDataFieldValue('companyName', null));
            yield put(actions.setDataFieldValue('title', null));
        }
        yield put(actions.setDataFieldValue('companyOptionsForm', null));
    } else {
        let companyOptionFormValue = {};
        if (preFillCompanyOptionsFormFields) {
            yield put(actions.setDataFieldValue('title', 'N/A'));
            yield delay(100);
            const companyOptionsForm = yield select(selectors.getDataField, 'companyOptionsForm');
            const companyOptionsFormFields = companyOptionsForm ? companyOptionsForm.fields : [];
            companyOptionsFormFields.forEach(field => {
                const { name, value } = field || {};
                companyOptionFormValue[name] = value;
            });
            yield put(actions.setDataFieldValue('title', 'N/A'));
            if (setFakeValidationAddressType) {
                const formMetaData = yield select(selectors.getMetadata);
                const companyNameValue = yield select(selectors.getDataFieldValue, 'companyName');
                const companyName = getValueByStorePath('0.fields.companyName', formMetaData) || {
                    constraints: [],
                };
                const payload = {
                    name: 'companyName',
                    validations: {
                        required: true,
                        constraints: [...companyName.constraints],
                        value: companyNameValue,
                    },
                };
                yield put(actions.setFakeValidation(payload));
            }
        }
        yield put(
            actions.addFieldItem({ name: 'companyOptionsForm', value: companyOptionFormValue }),
        );
    }
}

export const createSaga = (dialogName, onSubmit) => (storeName, formId, deliveryCountry) => {
    const types = getTypes(storeName);
    const reduxArtifacts = {
        storeName,
        types,
        selectors: getSelectors(storeName),
        actions: getActions(storeName),
    };
    let payload = null;
    if (deliveryCountry) {
        payload = { deliveryCountry };
    }

    return [
        setContext({ dialogName }),
        ...dynaForms.createSagaFor(
            reduxArtifacts,
            dynaFormsApi.getFormMetadata(formId, payload),
            onSubmit,
            getConfirmationFromModal,
            true,
        ),
        initAddressSuggestionWatchers(reduxArtifacts),
        createWatcher(
            globalTypes.RESET_FORMS_MESSAGES,
            reduxArtifacts.actions.resetGlobalMessage(),
        ),
        takeEvery(uiTypes.CLOSE_DIALOG_BY_NAME, handleCloseDialog, storeName),
        takeEvery(uiTypes.OPEN_DIALOG, handleOpenDialog),
        takeEvery(types.INITIALIZE, initializeAddressForm(reduxArtifacts)),
        takeEvery(types.SET_EDITING, setEditing(reduxArtifacts)),
        takeEvery(types.OPEN_FORM_IN_DIALOG, openFormInDialog(reduxArtifacts)),
        initAddressRelationsWatchers(reduxArtifacts, storeName),
        takeEvery(types.SET_DATA_FIELD_VALUE, onSetDataFieldValue),
    ];
};

export const billingSaga = createSaga(checkoutAddressForm.billingForm, billingSubmit);
export const shippingSaga = createSaga(checkoutAddressForm.shippingForm, deliverySubmit);
